// src/hooks/useChat.js
import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useAuthStore } from "../store/authStore";
import { unstable_batchedUpdates } from "react-dom";

const API_URL = process.env.API_URL || "http://localhost:8000";

export function useChat(navigateTo, route) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const { user } = useAuthStore();
  const isAuthed = !!user;

  // 1. API Helpers
  async function getAuthHeader() {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  useEffect(() => {
    if (route === "home" || route === "/") {
      setActiveConvId(null);
    }
  }, [route]);

  // 2. Load Conversations
  useEffect(() => {
    if (!isAuthed) {
      setConversations([]);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.access_token) loadConversations(data.session.access_token);
    });
  }, [isAuthed]);

  async function loadConversations(token) {
    try {
      const res = await fetch(`${API_URL}/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const convs = await res.json();
        setConversations(convs.map(c => ({
          id: c.id, 
          title: c.title, 
          createdAt: c.created_at, 
          messages: [], 
          loading: false,
        })));
      }
    } catch (e) { console.error("Failed to load conversations", e); }
  }

  // 3. Delete Logic
  async function deleteConversation(id) {
    const wasActive = (id === activeConvId);
    setConversations(prev => prev.filter(c => c.id !== id));

    if (wasActive) {
      setActiveConvId(null);
      if (navigateTo) navigateTo("/chat"); 
    }

    try {
      await fetch(`${API_URL}/conversations/${id}`, {
        method: "DELETE",
        headers: await getAuthHeader(),
      });
    } catch (e) { console.error("Failed to delete", e); }
  }

  // 4. Create New (FIXED: Lazy Creation)
  function createNewConversation(setShowAuth) {
    if (!isAuthed) {
      if (setShowAuth) setShowAuth(true);
      return null;
    }
    
    // Just reset the ID and go to the base URL.
    // We DO NOT add anything to 'conversations' yet.
    setActiveConvId(null);
    navigateTo("/chat");
    
    setTimeout(() => document.querySelector("textarea")?.focus(), 120);
    return null;
  }

  // 5. Open Existing
  async function openConversation(id) {
    setActiveConvId(id);
    const conv = conversations.find(c => c.id === id);
    
    // Ensure URL matches ID
    if (!route.includes(id)) navigateTo(`/chat/${id}`);

    // If we have messages, don't re-fetch
    if (conv && conv.messages.length > 0) return;

    try {
      const res = await fetch(`${API_URL}/messages/${id}`, {
        headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
      });
      if (!res.ok) throw new Error("API Error");
      const msgs = await res.json();
      
      setConversations(prev => prev.map(c => c.id === id ? { ...c, messages: msgs.map(m => ({ role: m.role, text: m.content, meta: m.extra_metadata || m.meta || {} })) } : c));
    } catch (e) { console.error("Failed to load messages", e); }
  }

  // 6. SENDING LOGIC (FIXED: Handles creation on first send)
  // 6. SENDING LOGIC (Final Version)
  async function sendFromChat(text, explicitId = null, setShowAuth) {
    if (!isAuthed) {
      if (setShowAuth) setShowAuth(true);
      return;
    }

    let id = explicitId || activeConvId;

    // --- A. HANDLE NEW CHAT (Create Temp ID) ---
    if (!id) {
      id = `${Date.now()}`; // Generate Temp ID
      const newConv = {
        id,
        title: text.slice(0, 30),
        createdAt: new Date().toISOString(),
        messages: [{ role: "user", text }],
        loading: true
      };

      // Add to state AND navigate immediately
      setConversations(prev => [newConv, ...prev]);
      setActiveConvId(id);
      navigateTo(`/chat/${id}`);
    } else {
      // --- B. HANDLE EXISTING CHAT ---
      setConversations(prev => prev.map(c =>
        c.id === id
          ? { ...c, messages: [...c.messages, { role: "user", text }], loading: true }
          : c
      ));
    }

    try {
      // If id is numeric (Temp), send null to backend
      const payloadId = (!id.includes("-") && id.match(/^\d+$/)) ? null : id;

      const res = await fetch(`${API_URL}/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
        body: JSON.stringify({ message: text, conversation_id: payloadId }),
      });

      const j = await res.json();
      const realId = j.conversation_id;

      // --- C. THE CRITICAL SWAP (Fixing the bug) ---
      // If we got a real ID back, and it's different from the current ID (which means current ID is temp)
      if (realId && realId !== id) {
        unstable_batchedUpdates(() => {
          // 1. Update the conversations list in state
          setConversations(prev => prev.map(c => {
            // Find the conversation with the TEMP ID and give it the REAL ID
            if (c.id === id) {
              return { ...c, id: realId };
            }
            return c;
          }));

          // 2. Update the active conversation ID in state
          setActiveConvId(realId);
        });

        // 3. Silently update the URL to the real UUID without reloading
        window.history.replaceState({}, "", `/chat/${realId}`);

        // IMPORTANT: Update the local 'id' variable so the streaming logic below uses the real ID
        id = realId;
      }

      // --- D. STREAMING SIMULATION (Append AI response) ---
      const fullText = j.recommendation || "";
      const laptopResults = j.results || []; 
      
      let i = 0;
      const interval = setInterval(() => {
        // 1. DEFINE CHAR HERE (This was missing!)
        const char = fullText[i];

        setConversations(currentConvs => currentConvs.map(c => {
          // Only update the active conversation
          if (c.id !== id) return c;
          
          const msgs = [...c.messages];
          const lastIndex = msgs.length - 1;
          const lastMsg = msgs[lastIndex];
          
          let updatedMsg;

          // Check if we need to add a NEW assistant message
          if (!lastMsg || lastMsg.role !== "assistant") {
             updatedMsg = { 
               role: "assistant", 
               text: char || "", 
               meta: { results: laptopResults } 
             };
             msgs.push(updatedMsg);
          } else {
            // UPDATE existing message
            updatedMsg = { 
              ...lastMsg, 
              text: lastMsg.text + (char || ""), // Now 'char' is defined!
              meta: { 
                 ...lastMsg.meta, 
                 results: laptopResults 
              } 
            };
            msgs[lastIndex] = updatedMsg; 
          }
          
          return { 
             ...c, 
             messages: msgs, 
             loading: i < fullText.length - 1 
          };
        }));
        
        i++;
        if (i >= fullText.length) clearInterval(interval);
      }, 10);

    } catch (e) {
      console.error(e);
      setConversations(prev => prev.map(c => c.id === id ? { ...c, loading: false } : c));
    }
  }

  return {
    conversations,
    activeConvId,
    createNewConversation,
    openConversation,
    sendFromChat,
    deleteConversation
  };
}